import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  getSnapshotById,
  getSnapshotBills,
  verifySnapshotIntegrity,
  formatCurrency
} from '../../services/monthEndCloseService';

// Styled Components
const HeaderCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(30, 58, 95, 0.3)',
  padding: theme.spacing(3),
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  '& .MuiTableCell-body': {
    fontSize: '0.85rem',
  },
}));

const SnapshotViewer = () => {
  const { snapshotId } = useParams();
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billsLoading, setBillsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalBills, setTotalBills] = useState(0);

  // Load snapshot
  const loadSnapshot = useCallback(async () => {
    if (!snapshotId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSnapshotById(snapshotId);
      setSnapshot(data);
    } catch (err) {
      console.error('Error loading snapshot:', err);
      setError('Failed to load snapshot');
    } finally {
      setLoading(false);
    }
  }, [snapshotId]);

  // Load bills
  const loadBills = useCallback(async () => {
    if (!snapshotId) return;
    setBillsLoading(true);
    try {
      const result = await getSnapshotBills(snapshotId, rowsPerPage, page * rowsPerPage);
      setBills(result.bills);
      setTotalBills(result.total);
    } catch (err) {
      console.error('Error loading bills:', err);
    } finally {
      setBillsLoading(false);
    }
  }, [snapshotId, page, rowsPerPage]);

  // Verify integrity
  const handleVerify = async () => {
    if (!snapshotId) return;
    setVerifying(true);
    try {
      const result = await verifySnapshotIntegrity(snapshotId);
      setVerification(result);
    } catch (err) {
      console.error('Error verifying snapshot:', err);
      setVerification({ valid: false, error: err.message });
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    if (snapshot) {
      loadBills();
    }
  }, [snapshot, loadBills]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  const getStatusChip = (status) => {
    const statusColors = {
      draft: 'default',
      pending: 'warning',
      approved: 'info',
      paid: 'success',
      partial: 'warning',
      overdue: 'error'
    };
    return (
      <Chip 
        label={status} 
        size="small" 
        color={statusColors[status?.toLowerCase()] || 'default'}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !snapshot) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">{error || 'Snapshot not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <HeaderCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton
            onClick={() => navigate('/billing/month-end-close')}
            sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Period Snapshot: {getMonthName(snapshot.month)} {snapshot.year}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {snapshot.facility_name || 'Facility'} • Closed {new Date(snapshot.closed_at).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Verify Integrity">
              <Button
                variant="outlined"
                size="small"
                startIcon={verifying ? <CircularProgress size={16} /> : <VerifiedIcon />}
                onClick={handleVerify}
                disabled={verifying}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              >
                Verify
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Verification Result */}
        {verification && (
          <Alert 
            severity={verification.valid ? 'success' : 'error'} 
            sx={{ mt: 2 }}
            icon={verification.valid ? <CheckCircleIcon /> : <ErrorIcon />}
          >
            {verification.valid 
              ? 'Snapshot integrity verified. Data matches original close.'
              : `Integrity check failed: ${verification.error || 'Data mismatch detected'}`
            }
          </Alert>
        )}
      </HeaderCard>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <ReceiptIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {snapshot.total_bills}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Bills</Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <MoneyIcon sx={{ fontSize: 32, mb: 1, color: '#4caf50' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                {formatCurrency(snapshot.total_revenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <MoneyIcon sx={{ fontSize: 32, mb: 1, color: '#2196f3' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                {formatCurrency(snapshot.total_payments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Payments</Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <MoneyIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                {formatCurrency(snapshot.outstanding_balance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Outstanding</Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Snapshot Details */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              Snapshot Information
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Snapshot ID</Typography>
                <Tooltip title={snapshot.snapshot_id} arrow placement="top">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {snapshot.snapshot_id?.slice(0, 10)}...
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Accounting Method</Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {snapshot.accounting_method}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Period Start</Typography>
                <Typography variant="body2">{snapshot.period_start_date}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Period End</Typography>
                <Typography variant="body2">{snapshot.period_end_date}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Closed By</Typography>
                <Typography variant="body2">{snapshot.closed_by_name || snapshot.closed_by}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Closed At</Typography>
                <Typography variant="body2">{new Date(snapshot.closed_at).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              Bill Status Breakdown
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Draft</Typography>
                <Typography variant="h6">{snapshot.bills_draft || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="h6">{snapshot.bills_pending || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Approved</Typography>
                <Typography variant="h6">{snapshot.bills_approved || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Paid</Typography>
                <Typography variant="h6" sx={{ color: 'success.main' }}>{snapshot.bills_paid || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Partial</Typography>
                <Typography variant="h6" sx={{ color: 'warning.main' }}>{snapshot.bills_partial || 0}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Overdue</Typography>
                <Typography variant="h6" sx={{ color: 'error.main' }}>{snapshot.bills_overdue || 0}</Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Insurance Revenue</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(snapshot.insurance_revenue || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Self-Pay Revenue</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(snapshot.self_pay_revenue || 0)}
                </Typography>
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Bills Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bills at Close ({totalBills})
          </Typography>
          <IconButton onClick={loadBills} disabled={billsLoading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {billsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <StyledTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bill #</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>MRN</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills.map((bill) => (
                    <TableRow key={bill.bill_id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {bill.bill_number || bill.bill_id?.slice(0, 8)}
                      </TableCell>
                      <TableCell>{bill.patient_name || 'N/A'}</TableCell>
                      <TableCell>{bill.patient_mrn || 'N/A'}</TableCell>
                      <TableCell>
                        {bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>{getStatusChip(bill.status)}</TableCell>
                      <TableCell align="right">{formatCurrency(bill.total_amount)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(bill.paid_amount)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: bill.balance_due > 0 ? 'warning.main' : 'text.primary' }}>
                        {formatCurrency(bill.balance_due)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {bills.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No bills in this snapshot</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </StyledTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalBills}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SnapshotViewer;

